/**
 * ontology-words.ts — the ontology's server-side enums as a moderator reads
 * them: each group's members by their keys, written whole, and the label a
 * value takes — its words when the portal knows the member, else the raw
 * identifier, which keeps the screen readable if the server adds one. The
 * evaluation screen and its draft cards read the same labels from here.
 */

import type { TFunction } from 'i18next';
import type {
  OntologyActionSource,
  OntologyEvaluationStatus,
  OntologyEvidenceRefType,
  OntologyObjectRoleKind,
  OntologySessionStatus,
  OntologyTranscriptStatus,
} from '../../api/moderator/ontology/types';

/**
 * Each enum's labels, by group and member. The `satisfies` holds every member
 * of each enum to a key, so one the API types gain cannot go unlabelled.
 */
const ONTOLOGY_ENUM_KEY = {
  sessionStatus: {
    INGESTING: 'evaluation.ontology.sessionStatus.INGESTING',
    COMPLETE: 'evaluation.ontology.sessionStatus.COMPLETE',
    INCOMPLETE: 'evaluation.ontology.sessionStatus.INCOMPLETE',
  },
  transcriptStatus: {
    NOT_EXPECTED: 'evaluation.ontology.transcriptStatus.NOT_EXPECTED',
    OPEN: 'evaluation.ontology.transcriptStatus.OPEN',
    COMPLETE: 'evaluation.ontology.transcriptStatus.COMPLETE',
    INCOMPLETE: 'evaluation.ontology.transcriptStatus.INCOMPLETE',
  },
  source: {
    HUMAN: 'evaluation.ontology.source.HUMAN',
    BOT: 'evaluation.ontology.source.BOT',
    SYSTEM: 'evaluation.ontology.source.SYSTEM',
    MODERATOR: 'evaluation.ontology.source.MODERATOR',
  },
  objectRole: {
    CREATED: 'evaluation.ontology.objectRole.CREATED',
    EDITED: 'evaluation.ontology.objectRole.EDITED',
    DELETED: 'evaluation.ontology.objectRole.DELETED',
    AFFECTED: 'evaluation.ontology.objectRole.AFFECTED',
    CONTEXT: 'evaluation.ontology.objectRole.CONTEXT',
  },
  evaluationStatus: {
    DRAFT: 'evaluation.ontology.evaluationStatus.DRAFT',
    PUBLISHED: 'evaluation.ontology.evaluationStatus.PUBLISHED',
    SUPERSEDED: 'evaluation.ontology.evaluationStatus.SUPERSEDED',
  },
  evidenceType: {
    ACTION: 'evaluation.ontology.evidenceType.ACTION',
    DECISION: 'evaluation.ontology.evidenceType.DECISION',
    OBJECT_VERSION: 'evaluation.ontology.evidenceType.OBJECT_VERSION',
    SNAPSHOT: 'evaluation.ontology.evidenceType.SNAPSHOT',
    UTTERANCE: 'evaluation.ontology.evidenceType.UTTERANCE',
  },
} satisfies {
  sessionStatus: Record<OntologySessionStatus, string>;
  transcriptStatus: Record<OntologyTranscriptStatus, string>;
  source: Record<OntologyActionSource, string>;
  objectRole: Record<OntologyObjectRoleKind, string>;
  evaluationStatus: Record<OntologyEvaluationStatus, string>;
  evidenceType: Record<OntologyEvidenceRefType, string>;
};

/** A server-side enum's value in the moderator's language, by its group: `ontologyEnumLabel(t, 'source', 'BOT')`. */
export function ontologyEnumLabel(t: TFunction, group: keyof typeof ONTOLOGY_ENUM_KEY, value: string): string {
  const labels: Partial<Record<string, string>> = ONTOLOGY_ENUM_KEY[group];
  const key = labels[value];
  return key ? t(key) : value;
}
